/**
 * Org MFA enrolment policy (ENT-3463, spec decision D1).
 *
 * Decides whether the app must force org-MFA TOTP enrolment
 * (OrgMFAEnforcementScreen) for the current session. Decision D1: an SSO session
 * delegates MFA to the customer IdP, so it SATISFIES org MFA policy and is never
 * forced to enrol an app TOTP factor. Password-session enforcement is unchanged.
 *
 * Pure logic — no Supabase calls; the single source of truth for the SSO MFA
 * exemption used by AppLayout and the auth flow controller.
 */

import { isSsoSession, type SsoSessionLike } from './ssoSession';

export interface OrgMfaEnrollmentInput {
  /** Current auth session (or null when unauthenticated). */
  session: SsoSessionLike | null | undefined;
  /** Org policy: `organizations.settings.mfa_required`. */
  orgMfaRequired: boolean;
  /** Whether the user has a verified TOTP factor. */
  userHasVerifiedFactor: boolean;
  /** Environment preview/local MFA bypass (`isPreviewMfaBypass()`). */
  previewBypass: boolean;
  /** Effective requirement source selected by AppLayout. */
  mfaRequirementSource?: 'org' | 'super_admin' | 'super_admin_pending' | null;
}

/**
 * True when the app must force org-MFA TOTP enrolment for this session.
 *
 * Order of precedence (each preserves existing behaviour):
 *  1. preview/local bypass wins (parity with today);
 *  2. D1 — an SSO session delegates an organisation MFA policy to the IdP;
 *  3. otherwise the existing password-session rule: required AND no factor.
 */
export function isOrgMfaEnrollmentRequired(input: OrgMfaEnrollmentInput): boolean {
  if (input.previewBypass) return false;
  // D1 delegates only a customer's organisation policy to its IdP. Platform
  // super-admin enrolment remains mandatory even when that administrator used
  // SSO to sign in.
  if (input.mfaRequirementSource === 'org' && isSsoSession(input.session)) return false;
  return input.orgMfaRequired && !input.userHasVerifiedFactor;
}

/** Does an SSO session satisfy org MFA policy? (scenario predicate for D1.) */
export function ssoSessionSatisfiesOrgMfa(
  session: SsoSessionLike | null | undefined,
): boolean {
  return isSsoSession(session);
}
